import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

import { Wagon } from 'src/app/models/wagon.model';
import { ActivatedRoute, Router } from '@angular/router';
import { RequestService } from 'src/app/services/request.service';
import { ModalDialog } from 'src/app/Modal/modal';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { Variant } from 'src/app/models/variant.model';
import { ModalOverrideInput } from 'src/app/Modal/modalOverrideInput.modal';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';
import { AddVariantComponent } from './add-variant/add-variant.component';

@Component({
  selector: 'app-variant-list',
  templateUrl: './variant-list.component.html',
  styleUrls: ['./variant-list.component.css']
})
export class VariantListComponent implements OnInit, OnDestroy {
  //Properties
  private unscubscribe$: Subject<void> = new Subject<void>();
  displayedColumns: string[];
  dataSource = new MatTableDataSource;
  projectId: number;
  wagonId: number;
  wagons: Wagon[];
  selectedWagon;
  private routeSub: Subscription;

  variant = {
    title: 'Layout List',
    dropdownPlaceholder: 'Choose Carriage / Train',
    addButton: 'Add New Layout',
    col1Titel: 'Layout Name',
    col2Titel: 'Actions',
    action1: 'Edit Layout',
    action2: 'Evaluate',
    action3: 'Delete Layout',
    deleteDialog: 'Do you really want to delete this layout?'
  }

  // Ctor
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private resourceService: RequestService,
    private dialog: MatDialog
  ) {
  }

  // Lifecycle hooks
  ngOnInit() {
    this.routeSub = this.route.params.subscribe(params => {
      this.displayedColumns = ['variantName', 'variantActions'];
      this.projectId = parseInt(params['projectId'].toString());
      this.loadWagons(this.projectId);
      this.wagonId = parseInt(params['waggonId'].toString());
      this.selectedWagon = this.wagonId;
      this.loadVariants();
    });
  }

  loadWagons(projectID: number) {
    this.resourceService.getWagons(projectID)
      .pipe(take(1))
      .subscribe(next => {
        this.wagons = next;
      });
  }

  loadVariants() {
    this.resourceService.getVariants(this.wagonId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(next => {
        this.setVariantList(next);
      });
  }

  setVariantList(next: Variant[]) {
    if (next) {
      this.dataSource.data = next.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
    }
    else {
      this.dataSource.data = [];
    }
  }

  changeWagon(wagonId: number) {
    this.router.navigate(['layout/list', this.projectId, wagonId]);
  }

  editVariant(variantId: number) {
    this.router.navigate(['/editor', this.wagonId, variantId]);
  }

  showDeleteDialog(variantID: number): void {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Warning',
        message: this.variant.deleteDialog,
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Okay'
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(result => {
        if (result) {
          this.deleteVariant(variantID);
        } else {
          // do nothing just close the modal
        }
      });
  }

  createVariantDialog() {
    let dialogRef = this.dialog.open(AddVariantComponent, {
      data: { title: 'Please enter a layout name', layoutname: '' }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
      if (result.state && result.layoutname !== '') {
        this.checkIfNameExists(result.layoutname);
      }
      else if (result.state && result.layoutname === '') {
        let dialogRef1 = this.dialog.open(ModalDialog, {
          data: { title: 'Warning!', message: "Layout name can't be empty!", showCancel: false, saveButtonText: 'Ok' }
        });
      }
      else {
        // do nothing
      }
    });
  }

  checkIfNameExists(layoutname: string) {

    let variants = this.dataSource.data as Variant[];

    let variantWithLayoutName: Variant = variants.filter(f => f.name == layoutname)[0];

    if (variantWithLayoutName == undefined || variantWithLayoutName == null) {
      this.createVariant(layoutname);
    }
    else {
      this.openOverrideLayoutDialog(layoutname, variantWithLayoutName.id);
    }
  }

  openOverrideLayoutDialog(layoutname: string, variantId: number) {
    let dialogRef = this.dialog.open(ModalOverrideInput, {
      data: { text: 'A Variant with the name ' + layoutname + ' already exists in this Carriage. Do you want to override it?', layoutname: '', override: false, title: 'Warning!' }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((override: boolean) => {
      if (override == true) {
        this.deleteVariant(variantId);
        this.createVariant(layoutname);
      }
      else {
        this.createVariantDialog();
      }
    });
  }

  createVariant(layoutname: string) {
    let variant: Variant = new Variant();
    variant.name = layoutname;
    variant.wagonId = parseInt(this.wagonId.toString());
    this.resourceService.writeVariant(variant)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(next => {
        this.loadVariants();
      });
  }

  deleteVariant(variantID: number) {
    this.resourceService.deleteVariant(variantID)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe((next: Variant[]) => {
        this.setVariantList(next);
      });
  }

  ngOnDestroy(): void {
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
    this.routeSub.unsubscribe();
  }
}